// Procedural sound (Web Audio): every effect is synthesised at runtime — no audio files to ship or
// license. One shared "office" reverb gives everything the same room. Volumes come from Settings.
//
// Sounds are short and soft by design (docs/06 §7): they confirm actions, they never nag.

export type Sfx =
  | 'click'
  | 'place'
  | 'hop'
  | 'permit'
  | 'mana'
  | 'vp'
  | 'card'
  | 'stamp'
  | 'turn'
  | 'round'
  | 'error'
  | 'select'
  | 'coin'
  | 'fanfare'
  | 'reveal'
  | 'shuffle'
  // Resolutions and Annex rooms (P-17, P-18)
  | 'gavel'
  | 'brass'
  | 'coinSpin'
  | 'ting'
  | 'thud'
  | 'hammer'
  | 'creak'
  | 'hum'
  | 'wind'
  | 'fold'
  | 'whoosh';

interface Volumes {
  master: number;
  sfx: number;
  ambience: number;
}

class AudioEngine {
  private ctx: AudioContext | null = null;
  private master!: GainNode;
  private sfxBus!: GainNode;
  private ambBus!: GainNode;
  private verb!: ConvolverNode;
  private verbSend!: GainNode;
  private noise!: AudioBuffer;
  private ambience: AudioBufferSourceNode | null = null;
  private vol: Volumes = { master: 0.8, sfx: 0.8, ambience: 0.35 };
  private last = new Map<Sfx, number>();
  private wantAmbience = false;
  private hidden = false;
  private idleTimer = 0;

  /** Browsers only allow audio after a user gesture: create the graph lazily on the first one. */
  unlock(): void {
    if (this.ctx) {
      this.sync();
      return;
    }
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return;
    // 'balanced', not the default 'interactive': on phones the lowest-latency output wakes the audio
    // thread every few milliseconds (about a quarter of a CPU core, for the ambience alone), while
    // the ~20 ms it adds is inaudible for these sounds.
    const ctx = new AC({ latencyHint: 'balanced' });
    this.ctx = ctx;
    this.master = ctx.createGain();
    this.master.connect(ctx.destination);
    this.sfxBus = ctx.createGain();
    this.ambBus = ctx.createGain();
    this.sfxBus.connect(this.master);
    this.ambBus.connect(this.master);
    this.verb = ctx.createConvolver();
    this.verb.buffer = this.impulse(1.4, 3);
    this.verbSend = ctx.createGain();
    this.verbSend.gain.value = 0.22;
    this.verbSend.connect(this.verb);
    this.verb.connect(this.sfxBus);
    this.noise = this.makeNoise(2);
    this.apply();
    if (this.wantAmbience) this.startAmbience();
  }

  /**
   * Silence everything while the page is in the background (a minimised app, another tab, the
   * phone locked) and pick up where it left off on return. Suspending the context also stops the
   * ambience loop and saves battery.
   */
  setBackground(hidden: boolean): void {
    this.hidden = hidden;
    this.sync();
  }

  setVolumes(v: Partial<Volumes>): void {
    this.vol = { ...this.vol, ...v };
    this.apply();
  }

  private apply(): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    this.master.gain.setTargetAtTime(this.vol.master, t, 0.05);
    this.sfxBus.gain.setTargetAtTime(this.vol.sfx, t, 0.05);
    this.ambBus.gain.setTargetAtTime(this.vol.ambience, t, 0.3);
    this.sync();
  }

  /**
   * Keep the context running only while it can be heard: a running context costs CPU even when
   * silent. Suspended at once in the background; when muted, once the master fade has run out.
   */
  private sync(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    clearTimeout(this.idleTimer);
    if (!this.hidden && this.vol.master > 0) {
      if (ctx.state === 'suspended') void ctx.resume();
    } else if (ctx.state === 'running') {
      if (this.hidden) void ctx.suspend();
      else this.idleTimer = window.setTimeout(() => void ctx.suspend(), 400);
    }
  }

  /** Room tone for the Ministry: soft brown noise, low-passed, with a slow breathing swell. */
  ambienceOn(on: boolean): void {
    this.wantAmbience = on;
    if (!this.ctx) return;
    if (on) this.startAmbience();
    else this.stopAmbience();
  }

  private startAmbience(): void {
    const ctx = this.ctx!;
    if (this.ambience) return;
    const src = ctx.createBufferSource();
    src.buffer = this.brown(6);
    src.loop = true;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 520;
    const g = ctx.createGain();
    g.gain.value = 0;
    g.gain.setTargetAtTime(0.16, ctx.currentTime, 1.5);
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.07;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 0.04;
    lfo.connect(lfoGain).connect(g.gain);
    lfo.start();
    src.connect(lp).connect(g).connect(this.ambBus);
    src.start();
    this.ambience = src;
  }

  private stopAmbience(): void {
    try {
      this.ambience?.stop();
    } catch {
      /* already stopped */
    }
    this.ambience = null;
  }

  play(name: Sfx, opts: { pitch?: number; gain?: number } = {}): void {
    const ctx = this.ctx;
    if (!ctx || this.vol.master <= 0 || this.vol.sfx <= 0 || document.hidden) return;
    // Rate-limit identical sounds so bursts (4 permits flying in) don't stack into noise.
    const now = performance.now();
    const minGap: Partial<Record<Sfx, number>> = { permit: 60, mana: 70, click: 40, card: 60, coin: 70, hammer: 45, thud: 60 };
    if (now - (this.last.get(name) ?? 0) < (minGap[name] ?? 20)) return;
    this.last.set(name, now);
    const t = ctx.currentTime + 0.005;
    const p = opts.pitch ?? 1;
    const g = opts.gain ?? 1;
    switch (name) {
      case 'click':
        this.tone(t, 1150 * p, 0.035, 0.05 * g, 'triangle');
        this.burst(t, 0.02, 2600, 0.05 * g, 'bandpass');
        break;
      case 'select':
        this.tone(t, 700 * p, 0.06, 0.06 * g, 'sine');
        this.tone(t + 0.04, 1050 * p, 0.08, 0.05 * g, 'sine');
        break;
      case 'hop':
        this.sweep(t, 0.22, 500, 1800, 0.04 * g);
        break;
      case 'place': // a wooden meeple landing on the floor
        this.knock(t, 170 * p, 0.14 * g);
        this.knock(t + 0.07, 210 * p, 0.06 * g);
        break;
      case 'permit': // a paper slip flicked into the tray
        this.burst(t, 0.07, 4200 * p, 0.09 * g, 'highpass');
        this.tone(t + 0.01, 880 * p, 0.05, 0.035 * g, 'triangle');
        break;
      case 'mana': {
        const notes = [1318.5, 1567.98, 1760, 2093];
        const f = notes[Math.floor(Math.random() * notes.length)]! * p;
        this.bell(t, f, 0.7, 0.05 * g);
        break;
      }
      case 'coin':
      case 'vp':
        this.bell(t, 1975 * p, 0.5, 0.05 * g);
        this.bell(t + 0.05, 2637 * p, 0.6, 0.04 * g);
        this.bell(t + 0.11, 3136 * p, 0.8, 0.03 * g);
        break;
      case 'card':
        this.sweep(t, 0.2, 900, 3400, 0.07 * g);
        break;
      case 'stamp': // rubber stamp: a dull thud and a paper slap
        this.knock(t, 95 * p, 0.28 * g, 0.18);
        this.burst(t, 0.05, 1400, 0.12 * g, 'bandpass');
        break;
      case 'turn':
        this.bell(t, 659.25 * p, 1.2, 0.07 * g);
        this.bell(t + 0.12, 987.77 * p, 1.4, 0.06 * g);
        break;
      case 'round': // the Ministry clock
        this.bell(t, 392 * p, 2.2, 0.1 * g, 3.5);
        this.bell(t + 0.02, 784 * p, 1.6, 0.04 * g, 3.5);
        break;
      case 'error':
        this.tone(t, 150, 0.12, 0.06 * g, 'square', 700);
        this.tone(t + 0.1, 118, 0.14, 0.06 * g, 'square', 700);
        break;
      case 'reveal':
        this.sweep(t, 0.35, 300, 2200, 0.05 * g);
        this.bell(t + 0.25, 1046.5 * p, 0.9, 0.05 * g);
        break;
      case 'shuffle': // a riffle: card edges flicking past each other, then the deck squared up
        for (let i = 0; i < 16; i++) this.burst(t + i * 0.028 + (i % 3) * 0.004, 0.018, 2600 + (i % 4) * 500, 0.05 * g, 'bandpass');
        this.knock(t + 0.52, 180 * p, 0.08 * g, 0.06);
        break;
      case 'fanfare':
        [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => this.bell(t + i * 0.13, f, 1.6, 0.07 * g, 2));
        this.bell(t + 0.55, 1318.5, 2.4, 0.06 * g, 2);
        break;
      case 'gavel': // two raps of the Grand Council's gavel on its block
        this.knock(t, 120 * p, 0.3 * g, 0.2);
        this.knock(t + 0.17, 132 * p, 0.34 * g, 0.24);
        this.burst(t + 0.17, 0.06, 900, 0.1 * g, 'bandpass');
        break;
      case 'brass': // a brass plaque: a bright, ringing strike
        this.bell(t, 880 * p, 1.8, 0.07 * g, 2.4);
        this.bell(t + 0.01, 1318.5 * p, 1.4, 0.04 * g, 2.4);
        this.bell(t + 0.2, 1760 * p, 1.2, 0.03 * g, 2.4);
        break;
      case 'coinSpin': // a coin flicked up: quick metallic ticks, rising
        for (let i = 0; i < 9; i++) this.bell(t + i * 0.045, (2400 + i * 140) * p, 0.08, 0.018 * g, 3.1);
        break;
      case 'ting': // heads: a clear high ting
        this.bell(t, 2637 * p, 1.1, 0.06 * g, 2.1);
        this.bell(t + 0.06, 3520 * p, 0.9, 0.035 * g, 2.1);
        break;
      case 'thud': // tails: the coin lands flat
        this.knock(t, 210 * p, 0.12 * g, 0.1);
        this.bell(t + 0.01, 1400 * p, 0.25, 0.015 * g, 3.3);
        break;
      case 'hammer': // a builder's hammer on timber
        this.knock(t, 380 * p, 0.16 * g, 0.07);
        this.burst(t, 0.03, 3200, 0.06 * g, 'bandpass');
        break;
      case 'creak': // a hanging sign swinging on its hinges
        this.creak(t, 0.55, 190 * p, 150 * p, 0.035 * g);
        this.creak(t + 0.5, 0.4, 170 * p, 210 * p, 0.022 * g);
        break;
      case 'hum': // the lights come on: a warm electric hum, then a small chime
        this.hum(t, 0.9, 110 * p, 0.05 * g);
        this.bell(t + 0.12, 1046.5 * p, 1.1, 0.035 * g, 2);
        break;
      case 'wind': // fog clearing: a long soft gust
        this.gust(t, 1.4, 0.06 * g);
        break;
      case 'fold': // paper folded into an aeroplane
        for (let i = 0; i < 4; i++) this.burst(t + i * 0.05, 0.04, 2200 + i * 400, 0.06 * g, 'bandpass');
        break;
      case 'whoosh':
        this.sweep(t, 0.4, 400, 3000, 0.06 * g);
        break;
    }
  }

  /** A rising or falling scrape of filtered sawtooth (hinges, wood under load). */
  private creak(t: number, dur: number, from: number, to: number, peak: number): void {
    const ctx = this.ctx!;
    const o = ctx.createOscillator();
    o.type = 'sawtooth';
    o.frequency.setValueAtTime(from, t);
    o.frequency.linearRampToValueAtTime(to, t + dur);
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 900;
    bp.Q.value = 4;
    const g = this.out(0.3);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + dur * 0.3);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(bp).connect(g);
    o.start(t);
    o.stop(t + dur + 0.05);
  }

  /** A mains hum that swells in and settles. */
  private hum(t: number, dur: number, f: number, peak: number): void {
    const ctx = this.ctx!;
    const g = this.out(0.2);
    this.env(g, t, peak, dur, 0.08);
    for (const [mult, amp] of [
      [1, 1],
      [2, 0.45],
      [3, 0.2],
    ] as const) {
      const o = ctx.createOscillator();
      o.frequency.value = f * mult;
      const pg = ctx.createGain();
      pg.gain.value = amp;
      o.connect(pg).connect(g);
      o.start(t);
      o.stop(t + dur + 0.05);
    }
  }

  /** A soft, slow gust of low-passed noise. */
  private gust(t: number, dur: number, peak: number): void {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(300, t);
    lp.frequency.exponentialRampToValueAtTime(1600, t + dur * 0.5);
    lp.frequency.exponentialRampToValueAtTime(400, t + dur);
    const g = this.out(0.6);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + dur * 0.45);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(lp).connect(g);
    src.start(t, Math.random());
    src.stop(t + dur + 0.05);
  }

  // ── primitives ─────────────────────────────────────────────────────────────

  private out(dry: number): GainNode {
    const ctx = this.ctx!;
    const g = ctx.createGain();
    g.connect(this.sfxBus);
    const send = ctx.createGain();
    send.gain.value = dry;
    g.connect(send).connect(this.verbSend);
    return g;
  }

  private env(g: GainNode, t: number, peak: number, dur: number, attack = 0.004): void {
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak), t + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  }

  private tone(t: number, f: number, dur: number, peak: number, type: OscillatorType, lowpass?: number): void {
    const ctx = this.ctx!;
    const o = ctx.createOscillator();
    o.type = type;
    o.frequency.value = f;
    const g = this.out(0.5);
    this.env(g, t, peak, dur);
    if (lowpass) {
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = lowpass;
      o.connect(lp).connect(g);
    } else o.connect(g);
    o.start(t);
    o.stop(t + dur + 0.05);
  }

  /** Inharmonic partials with a long exponential tail. */
  private bell(t: number, f: number, dur: number, peak: number, ratio = 2.76): void {
    const ctx = this.ctx!;
    const g = this.out(1.2);
    this.env(g, t, peak, dur, 0.003);
    for (const [mult, amp] of [
      [1, 1],
      [ratio, 0.35],
      [5.4, 0.12],
    ] as const) {
      const o = ctx.createOscillator();
      o.frequency.value = f * mult;
      const pg = ctx.createGain();
      pg.gain.value = amp;
      o.connect(pg).connect(g);
      o.start(t);
      o.stop(t + dur + 0.05);
    }
  }

  /** A short pitched knock (wood on wood). */
  private knock(t: number, f: number, peak: number, dur = 0.12): void {
    const ctx = this.ctx!;
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(f * 1.8, t);
    o.frequency.exponentialRampToValueAtTime(f, t + 0.03);
    const g = this.out(0.35);
    this.env(g, t, peak, dur, 0.002);
    o.connect(g);
    o.start(t);
    o.stop(t + dur + 0.05);
    this.burst(t, 0.025, 1800, peak * 0.5, 'bandpass');
  }

  private burst(t: number, dur: number, freq: number, peak: number, type: BiquadFilterType): void {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    const f = ctx.createBiquadFilter();
    f.type = type;
    f.frequency.value = freq;
    f.Q.value = 0.9;
    const g = this.out(0.3);
    this.env(g, t, peak, dur, 0.002);
    src.connect(f).connect(g);
    src.start(t, Math.random());
    src.stop(t + dur + 0.05);
  }

  /** Filtered-noise swish (cards, hops). */
  private sweep(t: number, dur: number, from: number, to: number, peak: number): void {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    const f = ctx.createBiquadFilter();
    f.type = 'bandpass';
    f.Q.value = 1.4;
    f.frequency.setValueAtTime(from, t);
    f.frequency.exponentialRampToValueAtTime(to, t + dur);
    const g = this.out(0.4);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + dur * 0.4);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(f).connect(g);
    src.start(t, Math.random());
    src.stop(t + dur + 0.05);
  }

  private makeNoise(seconds: number): AudioBuffer {
    const ctx = this.ctx!;
    const b = ctx.createBuffer(1, ctx.sampleRate * seconds, ctx.sampleRate);
    const d = b.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    return b;
  }

  private brown(seconds: number): AudioBuffer {
    const ctx = this.ctx!;
    const b = ctx.createBuffer(2, ctx.sampleRate * seconds, ctx.sampleRate);
    for (let ch = 0; ch < 2; ch++) {
      const d = b.getChannelData(ch);
      let last = 0;
      for (let i = 0; i < d.length; i++) {
        last = (last + 0.02 * (Math.random() * 2 - 1)) / 1.02;
        d[i] = last * 3.5;
      }
      // Crossfade the loop seam.
      const fade = Math.floor(ctx.sampleRate * 0.25);
      for (let i = 0; i < fade; i++) {
        const k = i / fade;
        d[i] = d[i]! * k + d[d.length - fade + i]! * (1 - k);
      }
    }
    return b;
  }

  private impulse(seconds: number, decay: number): AudioBuffer {
    const ctx = this.ctx!;
    const len = ctx.sampleRate * seconds;
    const b = ctx.createBuffer(2, len, ctx.sampleRate);
    for (let ch = 0; ch < 2; ch++) {
      const d = b.getChannelData(ch);
      for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay);
    }
    return b;
  }
}

export const audio = new AudioEngine();

// Unlock on the first gesture; a soft click on every button press gives the UI tactile feedback.
if (typeof window !== 'undefined') {
  const unlock = () => audio.unlock();
  window.addEventListener('pointerdown', unlock, { capture: true });
  window.addEventListener('keydown', unlock, { capture: true });
  const background = () => audio.setBackground(document.hidden);
  document.addEventListener('visibilitychange', background);
  window.addEventListener('pagehide', () => audio.setBackground(true));
  window.addEventListener('pageshow', background);
  window.addEventListener(
    'click',
    (e) => {
      const b = (e.target as HTMLElement | null)?.closest?.('button');
      if (b && !(b as HTMLButtonElement).disabled && !b.classList.contains('silent')) audio.play('click');
    },
    { capture: true },
  );
}
